
import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
} from "firebase/auth";
import {
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { createContext, useContext, useEffect, useState } from "react";
import { auth, db, googleProvider } from "../firebase";
import { registerFcmToken } from "../services/fcmService";
import { createFreeSubscription } from "../utils/createFreeSubscription";
import { clearAnnouncementSessionFlags } from "../services/announcementService";

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [globalUser, setGlobalUser] = useState(null);
  const [globalData, setGlobalData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);



  // Delegates to the shared fcmService helper which:
  //  - Dedupes existing fcmTokens by token string (preserves Flutter mobile tokens)
  //  - Registers a single foreground onMessage listener (unsubscribing the prior one)
  const saveFcmToken = async (user, userType) => {
    if (!user || !userType) {
      console.log("Cannot save FCM token: User or userType missing.");
      return;
    }
    return registerFcmToken(user, userType);
  };


  const signup = (email, password) => {
    setError(null);
    return createUserWithEmailAndPassword(auth, email, password);
  };

  const login = (email, password) => {
    setError(null);
    console.log("Logging in with email and password");
    return signInWithEmailAndPassword(auth, email, password);
  };

  const logOut = async () => {

    setGlobalUser(null);
    setGlobalData(null);
    localStorage.removeItem("userType"); // Clear user type on logout
    localStorage.removeItem("userRole"); // Clear selected role on logout
    localStorage.removeItem("accessTokenGoogle");
    localStorage.removeItem('mobileWithCountryCode');
    localStorage.removeItem('user');
    clearAnnouncementSessionFlags(); // so `everyLogin` announcements re-show on next login
    setError(null);
    try {
      await signOut(auth);
      window.location.href = `${process.env.PUBLIC_URL}/portal/onboarding`; // Redirect after successful sign out
    } catch (error) {
      console.error("Error signing out:", error);
      setError("Error signing out. Please try again.");

    }
  };


  const resetPassword = (email) => {
    setError(null);
    return sendPasswordResetEmail(auth, email);
  };

  // *** REVISED googleLogin Function ***
  const googleLogin = async (userType, way) => {
    console.log(`Google sign-in process started for ${userType}, mode: ${way}`);
    setError(null); // Clear previous errors

    try {
      // Sign out any existing sessions to ensure a clean popup flow
      // Consider if this is always desired. Might disrupt unrelated tabs.
      // await signOut(auth); // Let's comment this out unless absolutely necessary

      // Sign in with Google Popup
      const result = await signInWithPopup(auth, googleProvider);
      const credential = GoogleAuthProvider.credentialFromResult(result);
      const accessToken = credential?.accessToken; // Use optional chaining
      const user = result.user;

      console.log("User details from Google sign-in:", user);

      if (!user) {
        throw new Error("Google sign-in failed: No user data received.");
      }

      const mobileNumber = user.phoneNumber || null; // Get phone number, default to null if not available
      const collection = userType === "client" ? "client" : "lawyers";
      const otherCollection = userType === "client" ? "lawyers" : "client";

      // Reference to the potential document in the target collection
      const docRef = doc(db, collection, user.uid);
      let docSnapshot = await getDoc(docRef); // Check if document exists

      let userDataToSet = null; // This will hold the final data for global state

      // --- Scenario 1: Document ALREADY EXISTS in the target collection ---
      if (docSnapshot.exists()) {
        console.log(`Document found in '${collection}' collection for UID: ${user.uid}`);

        if (way === "signup") {
          // User trying to SIGN UP but account already exists in this role
          throw new Error(
            `Account already exists as a ${userType}. Please log in instead.`
          );
        } else {
          // User is LOGGING IN and account exists - proceed
          userDataToSet = docSnapshot.data();

          // Lawyer and Law Student share the "lawyers" collection, so make sure
          // the role chosen on the onboarding selector matches this account's
          // registered role before letting them in.
          if (userType === "lawyers") {
            const selectedRole =
              localStorage.getItem("userRole") === "student" ? "student" : "lawyer";
            const storedRole =
              userDataToSet.userCateogry === "Law student" ? "student" : "lawyer";
            if (selectedRole !== storedRole) {
              await signOut(auth);
              throw new Error(
                storedRole === "student"
                  ? "This account is registered as a Law Student. Please go back and select 'Law Student' to sign in."
                  : "This account is registered as a Lawyer. Please go back and select 'Lawyer' to sign in.",
              );
            }
            localStorage.setItem("userRole", storedRole);
          }

          console.log(`Existing ${userType} data:`, userDataToSet);
          console.log(`User logged in:`, {
            uid: user.uid,
            email: user.email,
            name: userDataToSet.name,
            mobileNumber: userDataToSet.mobileNumber,
            mobileWithCountryCode: userDataToSet.mobileWithCountryCode
          });
          // Optionally update accessToken or other fields if needed on login
          // await updateDoc(docRef, { lastLogin: serverTimestamp(), accessToken: accessToken });
        }
      }
      // --- Scenario 2: Document DOES NOT EXIST in the target collection ---
      else {
        console.log(`Document NOT found in '${collection}' collection for UID: ${user.uid}`);

        if (way === "login") {
          // User trying to LOG IN but account doesn't exist in this role
          throw new Error(
            `No ${userType} account found with this email. Please sign up first.`
          );
        } else {
          // User is SIGNING UP and account doesn't exist - proceed to create
          console.log(`Proceeding with signup for new ${userType}.`);

          // **Crucial Check**: Ensure user doesn't exist in the OTHER collection
          const otherDocRef = doc(db, otherCollection, user.uid);
          const otherDocSnapshot = await getDoc(otherDocRef);
          if (otherDocSnapshot.exists()) {
            throw new Error(
              `This email is already registered as a ${otherCollection}. Please use a different email or log in to your ${otherCollection} account.`
            );
          }

          // Define the data for the new document
          const newUserData = {
            uid: user.uid,
            email: user.email,
            name: user.displayName || "User",
            displayName: user.displayName || "User",
            mobileNumber: mobileNumber,
            createdAt: serverTimestamp(),
            ...(userType === "client" ? {
              clientId: "",
              experience: 0,
              address: "",
              location: "",
              profile: "",
              degree: "",
              lawyerId: "",
              firmName: "",
              representsFirm: "",
              privacy: "nnn",
              userCateogry: "",
              type: "",
            } : {
              experience: 0,
              // Role picked on the onboarding selector ("lawyer" | "student").
              // Lawyers and law students share the "lawyers" collection.
              userCateogry:
                localStorage.getItem("userRole") === "student"
                  ? "Law student"
                  : "Lawyer",
              type: "",
              address: "",
              location: "",
              profile: "",
              degree: "",
              lawyerId: "",
              firmName: "",
              privacy: "nnn",
              emailVerified: false,
            })
          };

          // Create the document in Firestore
          await setDoc(docRef, newUserData);
          console.log(`Successfully created new ${userType} document in Firestore.`);

          // Create free 30-day subscription for new user
          await createFreeSubscription(user.uid);

          userDataToSet = newUserData;

        }
      }

      // --- Post-Login/Signup Logic ---
      if (userDataToSet) {
        setGlobalData(userDataToSet);
        localStorage.setItem("userType", userType); // Store the determined user type
        if (accessToken) {
          localStorage.setItem("accessTokenGoogle", accessToken); // Store token if needed
        } else {
          localStorage.removeItem("accessTokenGoogle");
        }



        await saveFcmToken(user, userType);




        if (userType === "lawyers" && way === "signup") {
          // Redirect to phone verification for lawyers
          console.log(`Redirecting ${userType} to phone verification.`);

          const redirectTo = "/portal/phoneVerification"; // Adjust as needed
          return { user, redirectTo, success: true };
        }
        else if (userType === "client" && way === "signup") {
          // Redirect to client phone verification
          console.log(`Redirecting ${userType} to client home.`);
          const redirectTo = "/portal/phoneVerification"; // Adjust as needed
          return { user, redirectTo, success: true };
        } else if (userType === "lawyers" && way === "login") {
          // Check if mobile number exists for lawyers
          if (!userDataToSet.mobileNumber && !userDataToSet.mobileWithCountryCode) {
            console.log(`Mobile number not found for ${userType}, redirecting to phone verification.`);
            const redirectTo = "/portal/phoneVerification";
            return { user, redirectTo, success: true };
          } else {
            // Redirect to lawyer home
            console.log(`Redirecting ${userType} to lawyer home.`);
            const redirectTo = "/portal/Dashboard"; // Adjust as needed
            return { user, redirectTo, success: true };
          }
        }
        else if (userType === "client" && way === "login") {
          // Check if mobile number exists for clients
          if (!userDataToSet.mobileNumber && !userDataToSet.mobileWithCountryCode) {
            console.log(`Mobile number not found for ${userType}, redirecting to phone verification.`);
            const redirectTo = "/portal/phoneVerification";
            return { user, redirectTo, success: true };
          } else {
            // Redirect to client dashboard
            console.log(`Redirecting ${userType} to client dashboard.`);
            const redirectTo = "/portal/Dashboard"; // Adjust as needed
            return { user, redirectTo, success: true };
          }
        } else {
          console.error("Unexpected user type or login/signup mode.");
          throw new Error("Unexpected user type or login/signup mode.");
        }
      } else {
        // This path should ideally not be reached if logic above is correct
        throw new Error(`Failed to obtain or create ${userType} data.`);
      }

    } catch (error) {
      console.error("Google sign-in error:", error);
      // Handle specific Firebase Auth errors if needed
      if (error.code === 'auth/popup-closed-by-user') {
        setError("Google sign-in cancelled.");
      } else if (error.code === 'auth/account-exists-with-different-credential') {
        setError("An account already exists with this email address using a different sign-in method.");
      } else {
        setError(error.message || "An unknown error occurred during Google sign-in."); // Set user-friendly error message
      }

      throw error;
    }
  };


  // Client-specific Google sign-in wrapper
  const clientGoogleLogin = (way) => { // Removed userType param, it's fixed here
    return googleLogin("client", way);
  };

  // Lawyer-specific Google sign-in wrapper
  const lawyerGoogleLogin = (way) => { // Removed userType param, it's fixed here
    return googleLogin("lawyers", way);
  };



  const updateUserDetails = async (
    userId,
    userDetails,
    userType = "client" // Default to client if not provided, but better to pass explicitly
  ) => {
    setError(null);
    if (!userId || !userType) {
      console.error("UpdateUserDetails: Missing userId or userType");
      setError("Cannot update details: User information is missing.");
      return Promise.reject(new Error("Missing userId or userType"));
    }
    const collection = userType === "client" ? "client" : "lawyers";
    const docRef = doc(db, collection, userId);
    try {
      await updateDoc(docRef, {
        ...userDetails,
        updatedAt: serverTimestamp() // Add an updatedAt timestamp
      });
      // Optionally update local globalData state immediately
      setGlobalData(prevData => ({ ...prevData, ...userDetails }));
      return Promise.resolve(); // Indicate success
    } catch (err) {
      console.error("Error updating user details:", err);
      setError("Failed to update profile details.");
      return Promise.reject(err); // Indicate failure
    }
  };


  // True when the signed-in user is a law student (lives in the "lawyers"
  // collection but with userCateogry "Law student"). Use this anywhere in the
  // app to gate student-only features, e.g. const { isLawStudent } = useAuth().
  const isLawStudent =
    (globalData?.userCateogry || globalData?.userCategory) === "Law student";

  const value = {
    globalUser,
    setGlobalData, // Keep if manual setting is needed elsewhere
    globalData,
    isLawStudent,
    isLoading,
    // googleLogin, // Expose specific ones instead?
    clientGoogleLogin,
    lawyerGoogleLogin,
    signup,
    login,
    logOut,
    resetPassword,
    updateUserDetails,
    error,
    setError, // Expose setError if needed by components
  };

  // --- useEffect for Auth State Changes ---
  useEffect(() => {
    setIsLoading(true); // Start loading when checking auth state
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log("onAuthStateChanged triggered. Current auth user:", user?.uid || "None");
      setGlobalUser(user); // Set Firebase auth user object

      if (user) {
        // User is signed in
        let determinedUserType = localStorage.getItem("userType");
        let firebaseData = null;

        console.log(`Auth state changed for user ${user.uid}. Checking Firestore data. Stored userType: ${determinedUserType}`);

        // If userType is stored, try that collection first
        if (determinedUserType) {
          const collection = determinedUserType === "client" ? "client" : "lawyers";
          const docRef = doc(db, collection, user.uid);
          try {
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
              firebaseData = docSnap.data();

              console.log(`Found data in '${collection}' collection for user ${user.uid}:`, firebaseData);
              console.log(`Found data in primary collection '${collection}' based on localStorage.`);
            } else {
              console.log(`Document not found in primary collection '${collection}'. Clearing stored userType.`);
              // If not found where expected, clear the potentially wrong localStorage value
              localStorage.removeItem("userType");
              determinedUserType = null; // Reset for the next check
            }
          } catch (err) {
            console.error(`Error fetching document from ${collection}:`, err);
            setError(`Error fetching user data.`);
            determinedUserType = null; // Assume failure, try other collection
          }
        }

        // If data not found yet (either no stored type, or check failed/doc missing)
        // Try to determine type by checking both collections
        if (!firebaseData) {
          console.log("Attempting to determine user type by checking both collections.");
          try {
            const clientRef = doc(db, "client", user.uid);
            const clientSnap = await getDoc(clientRef);
            if (clientSnap.exists()) {
              firebaseData = clientSnap.data();
              determinedUserType = "client";
              console.log("Found user data in 'client' collection.");
              localStorage.setItem("userType", "client"); // Store the found type
            } else {
              const lawyerRef = doc(db, "lawyers", user.uid);
              const lawyerSnap = await getDoc(lawyerRef);
              if (lawyerSnap.exists()) {
                firebaseData = lawyerSnap.data();
                determinedUserType = "lawyers";
                console.log("Found user data in 'lawyers' collection.");
                localStorage.setItem("userType", "lawyers"); // Store the found type
              } else {
                console.warn(`User ${user.uid} authenticated but no corresponding document found in 'client' or 'lawyers' collections.`);
                // This could happen if Firestore doc creation failed after auth, or data deletion.
                setError("User profile data not found. Please contact support or try signing up again.");
                // Maybe force logout? logOut();
              }
            }
          } catch (err) {
            console.error("Error checking collections for user data:", err);
            setError("Error retrieving user profile.");
          }
        }

        // Set the global data state
        setGlobalData(firebaseData);

        // Save FCM token if user data and type were successfully determined
        if (firebaseData && determinedUserType) {
          console.log(`User authenticated and data found. Attempting to save FCM token for ${determinedUserType}.`);
          await saveFcmToken(user, determinedUserType);
        } else {
          console.log("Skipping FCM token save because user data or type could not be determined.");
        }

      } else {
        // User is signed out
        console.log("User is signed out.");
        setGlobalData(null); // Clear Firestore data
        localStorage.removeItem("userType"); // Clear stored type
        localStorage.removeItem("userRole"); // Clear stored role
        setError(null); // Clear any previous errors
      }

      setIsLoading(false); // Finish loading indicator
    });

    // Cleanup subscription on unmount
    return () => {
      console.log("Unsubscribing from onAuthStateChanged.");
      unsubscribe();
    };
  }, []); // Empty dependency array means this runs once on mount

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
